import { useCallback, useEffect, useRef, useState } from 'react';

import type { TourStop } from '#domain/TourStop.ts';
import type { RoutingSettings } from '#features/tourCreate/model/routingSettings.ts';
import {
    canCalculateSmartRoute,
    fetchSmartRoute,
    parseSmartRoute,
    type RoutingMode,
    type SmartRouteResult,
} from '#features/tourCreate/service/smartRoute.ts';

import { config } from '../../../config.ts';

export type SmartRouteStage = 'preparing' | 'calculating' | 'preview' | 'complete' | 'error';
type State = { key: string; stage?: SmartRouteStage; result?: SmartRouteResult };

const MINIMUM_MODAL_DURATION_MS = 1000;
const COMPLETION_DURATION_MS = 500;

const waitForDuration = (remaining: number, signal: AbortSignal) => {
    if (remaining <= 0 || signal.aborted) {
        return Promise.resolve();
    }
    return new Promise<void>(resolve => {
        const finish = () => {
            clearTimeout(timer);
            signal.removeEventListener('abort', finish);
            resolve();
        };
        const timer = setTimeout(finish, remaining);
        signal.addEventListener('abort', finish, { once: true });
    });
};

export const useSmartRoute = (stops: TourStop[], contextKey: string, enabled: boolean, settings?: RoutingSettings) => {
    const getKey = (routeStops: TourStop[]) =>
        JSON.stringify([
            contextKey,
            settings,
            routeStops.map(stop => [stop.id, stop.latitude, stop.longitude, stop.start, stop.end]),
        ]);
    const key = getKey(stops);
    const currentKey = useRef(key);
    currentKey.current = key;
    const request = useRef<AbortController>(undefined);
    const lastMode = useRef<RoutingMode>('truck');
    const [state, setState] = useState<State>();
    const cancel = useCallback(() => {
        request.current?.abort();
        request.current = undefined;
        setState(undefined);
    }, []);

    useEffect(() => {
        request.current?.abort();
        request.current = undefined;
        setState(previous => (enabled && previous?.key === key ? previous : undefined));
        return () => {
            request.current?.abort();
            request.current = undefined;
        };
    }, [key, enabled]);

    const calculate = async (mode: RoutingMode = 'truck') => {
        if (!enabled || !config.hereApiKey || !canCalculateSmartRoute(stops)) {
            return;
        }
        request.current?.abort();
        lastMode.current = mode;
        const controller = new AbortController();
        const startedAt = performance.now();
        request.current = controller;
        const isCurrent = () => request.current === controller && currentKey.current === key;
        const timeout = setTimeout(() => {
            if (isCurrent()) {
                controller.abort();
                request.current = undefined;
                setState({ key, stage: 'error' });
            }
        }, 45000);
        setState({ key, stage: 'preparing' });
        try {
            setState({ key, stage: 'calculating' });
            const response = settings
                ? await fetchSmartRoute(stops, config.hereApiKey, controller.signal, mode, settings)
                : await fetchSmartRoute(stops, config.hereApiKey, controller.signal, mode);
            if (!isCurrent() || controller.signal.aborted) {
                return;
            }
            setState({ key, stage: 'preview' });
            const result = settings ? parseSmartRoute(response, mode, settings) : parseSmartRoute(response, mode);
            clearTimeout(timeout);
            await waitForDuration(MINIMUM_MODAL_DURATION_MS - (performance.now() - startedAt), controller.signal);
            if (!isCurrent() || controller.signal.aborted) {
                return;
            }
            setState({ key, stage: 'complete' });
            await waitForDuration(COMPLETION_DURATION_MS, controller.signal);
            if (isCurrent() && !controller.signal.aborted) {
                setState({ key, result });
            }
        } catch {
            if (isCurrent() && !controller.signal.aborted) {
                setState({ key, stage: 'error' });
            }
        } finally {
            clearTimeout(timeout);
            if (request.current === controller) {
                request.current = undefined;
            }
        }
    };
    const current = enabled && state?.key === key ? state : undefined;
    return {
        calculate,
        retry: () => calculate(lastMode.current),
        cancel,
        stage: current?.stage,
        result: current?.result,
        canCalculate: enabled && Boolean(config.hereApiKey) && canCalculateSmartRoute(stops),
    };
};
